import './datatable.scss'
import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Box, Button, IconButton, TablePagination } from '@mui/material'
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined'
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useTranslation } from 'react-i18next'
import KybNoRowsOverlay from '../../../pages/kyb/components/KybNoRowsOverlay'
import { useFindKybsQuery } from '../../../services/guenoApi'
import CustomLoadingOverlay from '../datagrid/CustomLoadingOverlay'
import CustomColumnMenu from '../datagrid/CustomColumnMenu'
import useUserTableConfig from '../../hooks/useUserTableConfig'
import { getOrderedColumns, getVisibilityModel, updateListOrder } from './tableUtils/tableUtils'
import FilterMenu from '../FilterMenu/FilterMenu'
import { kybRiskLevelEnum, kybStatusEnum } from '../../../pages/kyb/kyb.enums'
import CircleIcon from '@mui/icons-material/Circle'
import { useDataTableColumnsKyb } from './ColumnsByCountry'

const DatatableKyb = ({
  page,
  setPage,
  setRowsPerPage,
  setModalOpen,
  setIsLoading,
  creationOnNoRows,
  documentNumber = '',
  setRiskLevel,
  riskLevel,
  status,
  setStatus,
  handleDispatch,
}) => {
  const { t } = useTranslation(['common', 'messages'])
  const { email } = useSelector((state) => state.login)
  const queryState = useSelector((state) => state.kyb?.filters)
  const { currentCountry, countrySegment } = useSelector((state) => state.kybCountry)

  const columns = useDataTableColumnsKyb(currentCountry)
  // const columns = useCommonColumnsKybDefault()

  const {
    config,
    saveConfig,
    loading: configLoading,
  } = useUserTableConfig({
    tableKey: 'kyb',
    defaultConfig: {
      sortModel: [{ field: 'createdAt', sort: 'desc' }],
      rowsPerPage: 25,
      pinnedColumns: { left: [], right: [] },
      columnOrder: columns.map((c) => c.field),
    },
  })

  const { field, sort } = useMemo(
    () => (config.sortModel?.length ? config.sortModel[0] : { field: '', sort: '' }),
    [config.sortModel],
  )

  const visibleColumnFields = useMemo(
    () => config.columnOrder.filter((field) => config.visibleColumns?.[field] !== false),
    [config.columnOrder, config.visibleColumns],
  )
  const getMaxRowsPerPage = () => {
    const columnsCount = visibleColumnFields.length
    if (columnsCount <= 5) return 50
    if (columnsCount <= 7) return 25
    if (columnsCount <= 9) return 15
    if (columnsCount <= 11) return 10
    return 5
  }
  const allRowsPerPageOptions = [5, 10, 15, 25, 50]
  const availableRowsPerPageOptions = allRowsPerPageOptions.filter((opt) => opt <= getMaxRowsPerPage())

  const finalRowsPerPageOptions =
    availableRowsPerPageOptions.length >= 2 ? availableRowsPerPageOptions : [5, Math.min(10, getMaxRowsPerPage())]

  const prevDynamicRowsRef = useRef(getMaxRowsPerPage())
  const isMountedRef = useRef(false)
  const isInitializedRef = useRef(false)

  useEffect(() => {
    if (!isMountedRef.current) {
      return
    }

    const dynamicRows = getMaxRowsPerPage()
    if (prevDynamicRowsRef.current !== dynamicRows) {
      setRowsPerPage(dynamicRows)
      if (config.rowsPerPage !== dynamicRows) {
        saveConfig({ rowsPerPage: dynamicRows })
      }
      prevDynamicRowsRef.current = dynamicRows
    }
  }, [visibleColumnFields.length])

  const { isFetching, isLoading, refetch, data } = useFindKybsQuery(
    {
      email: queryState.showMyAssignments ? email : null,
      limit: Math.min(config.rowsPerPage, getMaxRowsPerPage()),
      sortField: field,
      sortDirection: sort === 'desc' ? -1 : 1,
      countrySegment,
      columns: visibleColumnFields.join(','),
      ...queryState,
    },
    {
      skip: !isInitializedRef.current,
    },
  )

  const slots = {
    columnMenu: CustomColumnMenu,
    loadingOverlay: CustomLoadingOverlay,
  }

  if (creationOnNoRows) {
    slots.noRowsOverlay = KybNoRowsOverlay
  }

  const apiRef = useGridApiRef()

  const handleChangeRowsPerPage = (event) => {
    const requestedRows = parseInt(event.target.value, 10)
    const maxRows = getMaxRowsPerPage()
    const newRowsPerPage = Math.min(requestedRows, maxRows)
    setRowsPerPage(newRowsPerPage)
    saveConfig({ rowsPerPage: newRowsPerPage })
    setPage(0)
    setIsLoading(true)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    setIsLoading(true)
  }

  const orderedColumns = useMemo(
    () => getOrderedColumns(config.columnOrder, columns),
    [JSON.stringify(config.columnOrder), columns],
  )

  const handleColumnOrderChange = (params) => {
    const updatedOrder = updateListOrder(config.columnOrder, params)
    if (JSON.stringify(config.columnOrder) !== JSON.stringify(updatedOrder)) {
      saveConfig({ columnOrder: updatedOrder })
    }
  }

  const visibleColumnsModel = useMemo(
    () => getVisibilityModel(config.visibleColumns, columns),
    [config.visibleColumns, columns],
  )

  useEffect(() => {
    if (config.rowsPerPage) {
      const maxRows = getMaxRowsPerPage()
      const validRowsPerPage = Math.min(config.rowsPerPage, maxRows)
      setRowsPerPage(validRowsPerPage)

      if (config.rowsPerPage !== validRowsPerPage) {
        saveConfig({ rowsPerPage: validRowsPerPage })
      }
    }

    // Marcar como montado después de la configuración inicial
    isMountedRef.current = true
  }, [])

  // Marcar como inicializado solo cuando rowsPerPage y config estén sincronizados
  useEffect(() => {
    if (isMountedRef.current && config.rowsPerPage) {
      const maxRows = getMaxRowsPerPage()
      const validRowsPerPage = Math.min(config.rowsPerPage, maxRows)

      // Solo marcar como inicializado si el config ya tiene el valor correcto
      if (config.rowsPerPage === validRowsPerPage) {
        isInitializedRef.current = true
      }
    }
  }, [config.rowsPerPage, visibleColumnFields.length])

  useEffect(() => {
    if (!isMountedRef.current) {
      return
    }

    const newColumnOrder = columns.map((c) => c.field)
    if (JSON.stringify(config.columnOrder) !== JSON.stringify(newColumnOrder)) {
      saveConfig({
        columnOrder: newColumnOrder,
        visibleColumns: getVisibilityModel({}, columns),
      })
    }
  }, [currentCountry, columns])

  return (
    <Box style={{ width: '100%', height: '100%', overflowX: 'auto' }}>
      <Box className="filter-bar">
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="label" endIcon={<MenuOutlinedIcon />} onClick={() => setModalOpen(true)}>
            {t('common:filters')}
          </Button>
          <FilterMenu
            label={
              <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {t('common:riskLevel')}
                {riskLevel !== '' && <CircleIcon sx={{ color: '#0dde4e', fontSize: '0.6rem' }} />}
              </Box>
            }
            options={kybRiskLevelEnum.map((r) => ({ label: t(`common:${r}`), value: r }))}
            defaultValue={riskLevel}
            onApply={(value) => {
              setRiskLevel(value)
              handleDispatch({ riskLevel: value })
            }}
            onReset={() => {
              setRiskLevel('')
              handleDispatch({ riskLevel: '' })
            }}
          />
          <FilterMenu
            label={
              <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {t('common:status')}
                {status !== '' && status !== 'ALL' && <CircleIcon sx={{ color: '#0dde4e', fontSize: '0.6rem' }} />}
              </Box>
            }
            options={kybStatusEnum.map((s) => ({ label: t(`common:${s}`), value: s }))}
            defaultValue={status}
            onApply={(value) => {
              setStatus(value)
              handleDispatch({ status: value })
            }}
            onReset={() => {
              setStatus('ALL')
              handleDispatch({ status: 'ALL' })
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TablePagination
            labelRowsPerPage={[]}
            component="div"
            count={data?.pagination?.totalElements || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={Math.min(config.rowsPerPage || 25, getMaxRowsPerPage())}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={finalRowsPerPageOptions}
          />
          <Button
            variant="label"
            endIcon={<MenuOutlinedIcon />}
            onClick={() => apiRef.current.showPreferences('columns')}
          >
            {t('common:showHideColumns')}
          </Button>
          <IconButton onClick={refetch} disabled={isLoading} title={t('common:refresh')}>
            <RefreshIcon color="action" />
          </IconButton>
        </Box>
      </Box>
      <Box>
        <DataGridPro
          getRowHeight={() => 'auto'}
          getRowId={(row) => row._id || row.id}
          rows={Array.isArray(data?.data) ? data?.data : []}
          columns={orderedColumns}
          apiRef={apiRef}
          slots={slots}
          slotProps={{
            noRowsOverlay: { defaultDocumentNumber: documentNumber, isKyb: true },
          }}
          hideFooter={true}
          autoHeight
          loading={isFetching || isLoading || configLoading}
          sortingMode="server"
          sortModel={config.sortModel}
          onSortModelChange={(newSortModel) => saveConfig({ sortModel: newSortModel })}
          columnOrder={config.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          pinnedColumns={config.pinnedColumns}
          onPinnedColumnsChange={(newPinnedColumns) => saveConfig({ pinnedColumns: newPinnedColumns })}
          columnVisibilityModel={visibleColumnsModel}
          onColumnVisibilityModelChange={(newModel) => saveConfig({ visibleColumns: newModel })}
        />
      </Box>
    </Box>
  )
}

export default DatatableKyb
